import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ApiError, useAdaptersApi } from '../apis/adapters-apis';
import { Adapter } from '../types';
import { useToast } from "../components/ui/Toast";
import { Spinner } from "../components/ui/Spinner";
import { Card, CardContent } from "../components/ui/Card";
import AdapterForm, { AdapterFormRef } from './AdapterForm';
import { NavigationWarningDialog } from '../components/NavigationWarningDialog';
import { useNavigation } from '../contexts/NavigationContext';
import { useAuth } from '../contexts/AuthContext';

export const EditAdapterPage: React.FC = () => {
  const formRef = useRef<AdapterFormRef>(null);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const adaptersApi = useAdaptersApi();
  const { user } = useAuth();

  const [editedAdapter, setEditedAdapter] = useState<Adapter | null>(null);
  const [originalAdapter, setOriginalAdapter] = useState<Adapter | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(() => {
    return !(location.state as { startEditing?: boolean })?.startEditing;
  });
  const initialFetchMade = useRef(false);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [existingAdapterNames, setExistingAdapterNames] = useState<string[]>([]);
  const backButtonClicked = useRef(false);
  const historyStateAdded = useRef(false);

  const { 
    showNavigationWarning, 
    pendingNavigation, 
    setShowNavigationWarning, 
    setPendingNavigation 
  } = useNavigation();
  
  // const baseUrl = `https://${process.env.REACT_APP_ENVIRONMENT}.agenticgenie.click/external-adapter/`;
  const baseUrl = `${process.env.REACT_APP_API_URL}/external-adapter/`;

  const compareAdapters = (adapter1: Adapter | null, adapter2: Adapter | null) => {
    if (!adapter1 || !adapter2) return false;

    const normalizeAdapter = (adapter: Adapter) => ({
      ...adapter,
      adapterParameters: {
        ...adapter.adapterParameters,
        parameters: adapter.adapterParameters?.parameters?.map(param => ({
          ...param,
          key: param.key.trim(),
          description: param.description.trim(),
          options: param.options?.trim(),
          example: param.example?.trim()
        })) || []
      }
    });

    return JSON.stringify(normalizeAdapter(adapter1)) === JSON.stringify(normalizeAdapter(adapter2));
  };

  const hasUnsavedChanges = !isReadOnly && !compareAdapters(editedAdapter, originalAdapter);

  // Handle browser back button
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    const handlePopState = (e: PopStateEvent) => {
      if (hasUnsavedChanges) {
        backButtonClicked.current = true;
        setShowNavigationWarning(true);
        setPendingNavigation('/marketplace/admin/adapters');
      } else {
        navigate('/marketplace/admin/adapters');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    if (!historyStateAdded.current && hasUnsavedChanges) {
      window.history.pushState(null, '', window.location.pathname);
      historyStateAdded.current = true;
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasUnsavedChanges, navigate, setShowNavigationWarning, setPendingNavigation]);

  useEffect(() => {
    const fetchAdapterAndNames = async () => {
      if (id && !initialFetchMade.current) {
        initialFetchMade.current = true;
        try {
          const adapter = await adaptersApi.getAdapter(id);
          const allAdapters = await adaptersApi.getAdapters();

          setExistingAdapterNames(
            allAdapters
              .filter((a: Adapter) => a.adapterId !== id)
              .map((a: Adapter) => a.adapterName)
          );
          
          const allTags = new Set<string>();
          allAdapters.forEach((adapter: Adapter) => {
            adapter.adapterTags?.forEach(tag => {
              if (tag) allTags.add(tag);
            });
          });
          
          setTagSuggestions(Array.from(allTags));
  
          if (adapter) {
            const updatedAdapter = {
              ...adapter,
              adapterUrlEndpoint: adapter.adapterUrlEndpoint 
                ? (adapter.adapterUrlEndpoint.startsWith(baseUrl)
                    ? adapter.adapterUrlEndpoint
                    : baseUrl + adapter.adapterUrlEndpoint)
                : baseUrl,
              adapterTargetUrl: adapter.adapterTargetUrl || '',
              adapterParameters: {
                parameters: adapter.adapterParameters?.parameters?.map(param => ({
                  key: param.key || '',
                  description: param.description || '',
                  type: param.type || 'str',
                  options: param.options || '',
                  example: param.example || ''
                })) || [],
                testMode: adapter.adapterParameters?.testMode ?? true
              },
              adapterOutput: adapter.adapterOutput || { parameters: {} },
              accessControl: adapter.accessControl || {
                viewAccessType: 'Owner Only',
                viewAllowedUsers: [],
                editAccessType: 'Owner Only',
                editAllowedUsers: []
              }
            };
            setEditedAdapter(updatedAdapter);
            setOriginalAdapter(updatedAdapter);
          } else {
            addToast({
              title: "Error",
              description: "Adapter not found",
              variant: "destructive",
              duration: 4000
            });
            navigate('/marketplace/admin/adapters');
          }
        } catch (error: any) {
          console.error('Error fetching data:', error);
          
          if (error instanceof ApiError && error.status === 403) {
            navigate('/marketplace/unauthorized');
            return;
          }
          
          const errorMessage = error instanceof ApiError 
            ? error.message 
            : "Failed to fetch adapter details";
            
          addToast({
            title: "Error",
            description: errorMessage,
            variant: "destructive",
            duration: 5000
          });
          navigate('/marketplace/admin/adapters');
        } finally {
          setIsLoading(false);
        }
      }
    };
    fetchAdapterAndNames();
  }, [id, addToast, navigate, adaptersApi, baseUrl]);

  const handleSubmit = async () => {
    if (!editedAdapter) {
      addToast({
        title: "Error",
        description: "No adapter data available to save",
        variant: "destructive",
        duration: 4000
      });
      return;
    }

    const isValid = formRef.current?.validateForm();
    if (!isValid) {
      addToast({
        title: "Validation Error",
        description: "Please fill in all required fields correctly",
        variant: "destructive",
        duration: 4000
      });
      return;
    }
  
    try {
      const adapterToUpdate = {
        ...editedAdapter,
        adapterUrlEndpoint: editedAdapter.adapterUrlEndpoint
          ? (editedAdapter.adapterUrlEndpoint.startsWith(baseUrl)
              ? editedAdapter.adapterUrlEndpoint.trim()
              : baseUrl + editedAdapter.adapterUrlEndpoint.trim())
          : baseUrl,
        adapterTargetUrl: editedAdapter.adapterTargetUrl?.trim() || '',
        adapterName: editedAdapter.adapterName.trim(),
        adapterDescription: editedAdapter.adapterDescription.trim(),
        adapterParameters: {
          parameters: editedAdapter.adapterParameters?.parameters.map(param => ({
            key: param.key.trim(),
            description: param.description.trim(),
            type: param.type || 'str',
            options: param.options?.trim(),
            example: param.example?.trim()
          })) || [],
          testMode: editedAdapter.adapterParameters?.testMode ?? true
        },
        adapterOutput: editedAdapter.adapterOutput || { parameters: {} },
        adapterAnnotation: editedAdapter.adapterAnnotation || '',
        adapterTags: editedAdapter.adapterTags || [],
        isCore: editedAdapter.isCore ?? false,
        accessControl: editedAdapter.accessControl || {
          viewAccessType: 'Owner Only',
          viewAllowedUsers: [],
          editAccessType: 'Owner Only',
          editAllowedUsers: []
        }
      };
  
      if (editedAdapter.adapterAuthentication && editedAdapter.adapterAuthentication !== '••••••••') {
        adapterToUpdate.adapterAuthentication = editedAdapter.adapterAuthentication.trim();
      }
  
      const updatedAdapter = await adaptersApi.updateAdapter(adapterToUpdate);
      
      const normalizedAdapter = {
        ...updatedAdapter,
        adapterParameters: {
          parameters: updatedAdapter.adapterParameters?.parameters || [],
          testMode: updatedAdapter.adapterParameters?.testMode ?? true
        },
        adapterTags: updatedAdapter.adapterTags || []
      };
      
      setOriginalAdapter(normalizedAdapter);
      setEditedAdapter(normalizedAdapter);
      setIsReadOnly(true);
      
      addToast({
        title: "Success",
        description: `Adapter ${adapterToUpdate.adapterName} has been successfully updated`,
        variant: "default",
        duration: 4000
      });
  
      if (pendingNavigation) {
        navigate(pendingNavigation);
        setPendingNavigation(null);
      } else {
        navigate(`/marketplace/admin/adapters/test/${adapterToUpdate.adapterId}`);
      }
    } catch (error) {
      console.error('Error updating adapter:', error);
      addToast({
        title: "Error",
        description: "Failed to update adapter. Please try again.",
        variant: "destructive",
        duration: 4000
      });
    }
  };

  const handleDiscardChanges = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation(null);
    } else {
      setEditedAdapter(originalAdapter);
      setIsReadOnly(true);
    }
  };

  const handleConfirmNavigation = () => {
    setShowNavigationWarning(false);
    setEditedAdapter(originalAdapter);
    setIsReadOnly(true);
    
    if (backButtonClicked.current) {
      backButtonClicked.current = false;
      historyStateAdded.current = false;
      navigate('/marketplace/admin/adapters');
    } else if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
    backButtonClicked.current = false;
    
    if (backButtonClicked.current) {
      window.history.pushState(null, '', window.location.pathname);
    }
  };

  const handleNavigateBack = () => {
    if (hasUnsavedChanges) {
      setShowNavigationWarning(true);
      setPendingNavigation('/marketplace/admin/adapters');
    } else {
      navigate('/marketplace/admin/adapters');
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center my-8 gap-2">
        <Spinner className="border-blue-500" />
        <p className="text-center text-muted-foreground">Loading Adapter...</p>
      </div>
    );
  }

  if (!editedAdapter) {
    return (
      <Card className="max-w-2xl mx-auto m-4">
        <CardContent className="p-6">
          <p className="text-center text-muted-foreground">Adapter not found.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div data-has-unsaved-changes={hasUnsavedChanges}>
      <AdapterForm 
        ref={formRef}
        adapter={editedAdapter}
        originalAdapter={originalAdapter}
        onAdapterChange={setEditedAdapter}
        onSubmit={handleSubmit}
        onCancel={handleNavigateBack}
        isEditing={true}
        readOnly={isReadOnly}
        onEditClick={() => setIsReadOnly(false)}
        onDiscardChanges={handleDiscardChanges}
        hasUnsavedChanges={hasUnsavedChanges}
        setIsReadOnly={setIsReadOnly}
        tagSuggestions={tagSuggestions}
        existingAdapterNames={existingAdapterNames}
        currentUserId={user?.sub || ''}
      />
  
      <NavigationWarningDialog
        open={showNavigationWarning}
        onConfirm={handleConfirmNavigation}
        onCancel={handleCancelNavigation}
        title="Discard Changes?"
        description="You have unsaved changes that will be lost if you leave this page. Are you sure you want to discard them?"
      />
    </div>
  );
};

export default EditAdapterPage; 